import type { AuthorizationRequest } from "./authorization";

export type IdentityProviderId = "mock" | "chatoner-accounts";
export type IdentityProviderStatus = "active" | "planned";

export interface IdentityProfile {
  name: string;
  email: string;
}

export interface IdentityProvider {
  id: IdentityProviderId;
  label: string;
  status: IdentityProviderStatus;
  complete(request: AuthorizationRequest, identity: IdentityProfile): void;
}

function completeMockAuthorization(request: AuthorizationRequest, identity: IdentityProfile) {
  const now = Date.now();
  const normalizedEmail = (identity.email || "academy.user@example.com").trim().toLowerCase();
  let accountHash = 2166136261;
  for (const character of normalizedEmail) {
    accountHash ^= character.charCodeAt(0);
    accountHash = Math.imul(accountHash, 16777619);
  }
  const payload = {
    version: 1,
    // The same mock account keeps one stable subject when it enters a different Academy app.
    subject: `mock-account-${(accountHash >>> 0).toString(36)}`,
    name: identity.name || "Academy User",
    email: normalizedEmail,
    audience: request.clientId,
    role: request.role,
    workspaceId: `${request.clientId}-primary`,
    permissions: ["workspace:read", `${request.clientId}:use`],
    issuedAt: now,
    expiresAt: now + 8 * 60 * 60 * 1000,
  };
  const code = window.btoa(JSON.stringify(payload));
  const callback = new URL(request.returnTo);
  callback.searchParams.set("auth_code", code);
  callback.searchParams.set("state", request.state);
  callback.searchParams.delete("auth");
  window.location.assign(callback.toString());
}

export const mockIdentityProvider: IdentityProvider = {
  id: "mock",
  label: "Frontend mock identity",
  status: "active",
  complete: completeMockAuthorization,
};

export const chatonerAccountsProvider: IdentityProvider = {
  id: "chatoner-accounts",
  label: "Chatoner Accounts",
  status: "planned",
  complete() {
    throw new Error("Chatoner Accounts is planned but is not connected to Academy.");
  },
};

export function configuredIdentityProvider(): IdentityProvider {
  const configured = import.meta.env.VITE_IDENTITY_PROVIDER;
  return configured === "chatoner-accounts" ? chatonerAccountsProvider : mockIdentityProvider;
}

export function completeAuthorization(request: AuthorizationRequest, identity: IdentityProfile) {
  const provider = configuredIdentityProvider();
  if (provider.status !== "active") throw new Error(`${provider.label} is not available yet.`);
  provider.complete(request, identity);
}
